import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";

import { api } from "../../api/client";
import type { AdminDashboard, AdminEventRow } from "../../types";
import { formatEventType } from "../../utils/formatters";

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

export default function AdminDashboardScreen() {
  const [stats, setStats] = useState<AdminDashboard | null>(null);
  const [events, setEvents] = useState<AdminEventRow[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<AdminEventRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [dashboard, eventsResponse] = await Promise.all([
        api.get<AdminDashboard>("/api/admin/dashboard"),
        api.get<{ total: number; events: AdminEventRow[] }>("/api/admin/events"),
      ]);
      setStats(dashboard);
      setEvents(eventsResponse.events);
      setError(null);
    } catch (err) {
      setError((err as Error).message || "Failed to load dashboard");
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), 30_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500">Live snapshot, refreshes every 30 seconds.</p>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="On Shift" value={stats?.clocked_in.length ?? "…"} tone="green" />
        <StatCard label="On Break" value={stats?.on_break.length ?? "…"} tone="yellow" />
        <StatCard label="Off Shift" value={stats?.not_clocked_in.length ?? "…"} tone="gray" />
        <StatCard
          label="Events Today"
          value={stats?.todays_events_count ?? "…"}
          tone="blue"
        />
      </div>

      <section className="rounded-xl border border-gray-200 bg-white">
        <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Today's Activity</h2>
          <span className="text-xs text-gray-500">
            {events.length} shown · {stats?.todays_photos_count ?? 0} with photo
          </span>
        </header>
        <ul className="divide-y divide-gray-100 text-sm">
          {events.length === 0 && (
            <li className="px-4 py-6 text-center text-gray-400">No events yet today.</li>
          )}
          {events.map((ev) => (
            <li
              key={ev.id}
              className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-50"
              onClick={() => setSelectedEvent(ev)}
            >
              <div className="h-10 w-10 overflow-hidden rounded-full bg-gray-100">
                {ev.photo_url ? (
                  <img src={ev.photo_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                    pending
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{ev.employee_name ?? "?"}</p>
                <p className="text-xs text-gray-500">
                  {formatEventType(ev.event_type)} ·{" "}
                  {TIME_FMT.format(new Date(ev.timestamp))}
                  {ev.is_manual_entry && " · manual"}
                </p>
              </div>
              <div className="text-right text-xs text-gray-400">
                {ev.latitude !== null && ev.longitude !== null
                  ? `${ev.latitude.toFixed(3)}, ${ev.longitude.toFixed(3)}`
                  : "no GPS"}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {selectedEvent && (
        <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone: "green" | "yellow" | "gray" | "blue";
}) {
  const toneClass = {
    green: "bg-green-50 text-green-700",
    yellow: "bg-yellow-50 text-yellow-800",
    gray: "bg-gray-50 text-gray-700",
    blue: "bg-pme-light-blue/20 text-pme-blue",
  }[tone];
  return (
    <div className={["rounded-xl p-4", toneClass].join(" ")}>
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-3xl font-semibold">{value}</p>
    </div>
  );
}

function EventModal({
  event,
  onClose,
}: {
  event: AdminEventRow;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h3 className="text-base font-semibold">{event.employee_name}</h3>
          <button type="button" onClick={onClose} className="text-gray-400">
            <X size={16} strokeWidth={2} aria-hidden />
          </button>
        </header>
        <div className="mt-3 space-y-3 text-sm">
          <Row label="Event" value={formatEventType(event.event_type)} />
          <Row label="Time" value={new Date(event.timestamp).toLocaleString()} />
          <Row
            label="GPS"
            value={
              event.latitude !== null && event.longitude !== null
                ? `${event.latitude.toFixed(5)}, ${event.longitude.toFixed(5)} (±${event.gps_accuracy_m?.toFixed(0) ?? "?"}m)`
                : "not captured"
            }
          />
          <Row label="Manual entry" value={event.is_manual_entry ? "yes" : "no"} />
          {event.photo_url ? (
            <img
              src={event.photo_url}
              alt="Clock event"
              className="mt-2 w-full rounded-lg"
            />
          ) : (
            <p className="rounded bg-yellow-50 p-2 text-xs text-yellow-800">
              Photo not uploaded yet — may still be in flight from the device.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-xs uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-right text-gray-900">{value}</span>
    </div>
  );
}
